/**
 * 系统健康透视 — the leaf arithmetic every node in the model is built from.
 *
 * Split out of `model.ts` so the join/union logic there stays readable: this
 * file only knows how to turn ONE wire row into ONE `ObservedTraffic` /
 * `NodeMoney` / `HealthGrade`, and how to roll several of those up. It makes
 * no decisions about which rows exist or how they join.
 *
 * Three rules are enforced here rather than at the call sites, because each is
 * easy to get accidentally wrong in a way that reads as a fact:
 *
 *   1. A missing money slice produces `settled: false` with null figures, not
 *      zeros. See `NodeMoney` in `types.ts` — a 0% margin is the claim "we
 *      sold this at cost", which is a real and much worse statement than "the
 *      rating pipeline has not caught up yet".
 *   2. Grading goes through the shared `components/trafficHealth.ts`
 *      thresholds and nothing else. A second copy of the numbers is how this
 *      page and the traffic monitor would start disagreeing about the word
 *      "unhealthy" while both looked right in isolation.
 *   3. A downstream subject and a supply subject are graded on DIFFERENT
 *      counters — `gradeSkuTraffic` vs `gradeSupplyTraffic` below. There is no
 *      one `gradeTraffic` any more, deliberately: the single function was the
 *      thing that let one counter answer two questions.
 *
 * ── Two counters, two subjects, no arithmetic between them ──
 *
 * `ObservedTraffic` in `types.ts` states it in full. The short version, from
 * the gateway's aggregation SQL:
 *
 *   error_count        = status_code >= 400 OR lifecycle_status = 'failed'
 *   server_error_count = status_code >= 500 OR upstream_status_code >= 500
 *
 * `status_code` is the tenant-facing FINAL status, so a request the retry
 * ladder rescued is absent from `errors` and present in `serverErrors`.
 * Neither set contains the other and `errors - serverErrors` is not a
 * quantity — nothing here subtracts them or derives a third bucket from them.
 *
 * Neither the wire metrics nor the breakdown rows carry a `serverErrorRate` or
 * a `rateLimitedRate`; the gateway publishes only the counts. Both are divided
 * here, by the same request count the gateway divided `errorRate` by, so the
 * three rates are comparable.
 */

import type {
  BusinessSkuEconomicsRow,
  BusinessSkuEconomicsTotals,
  OpsTrafficBreakdownRow,
  OpsTrafficMetrics,
  RevenueByUpstreamRow,
} from '@nadoutong/contracts';
import {
  downstreamSeverity,
  slowFirstByteSeverity,
  supplyFailureFigures,
  supplySeverity,
  worstSeverity,
  type TrafficSeverity,
} from '../../components/trafficHealth.ts';
import { expectedRefusalCount, refusalGroups } from './refusalCodes.ts';
import type { RefusalGroup } from './refusalCodes.ts';
import {
  HEALTH_GRADE_SEVERITY,
  type HealthGrade,
  type NodeMoney,
  type ObservedTraffic,
} from './types.ts';

/** `count / total`, with the empty window reading 0 rather than NaN. */
function rate(count: number, total: number): number {
  return total > 0 ? count / total : 0;
}

/**
 * The wire shape of the refusal count, probed structurally.
 *
 * `refusedBeforeUpstreamCount` is being added to `OpsTrafficMetrics` as an
 * OPTIONAL field, so at any given moment a deployed gateway may or may not
 * publish it. `requestCount` is required here only so this stays a real
 * structural match against `OpsTrafficMetrics` rather than a weak type that
 * every object satisfies. The value type is deliberately wider than the
 * contract's so this keeps compiling whichever nullability the field lands
 * with.
 */
interface MaybeRefusalMetrics {
  readonly requestCount: number;
  readonly refusedBeforeUpstreamCount?: number | null;
}

/**
 * The refusal count, or `null` when this gateway does not report one.
 *
 * `null` is NOT zero and the difference is load-bearing: zero refusals is a
 * measurement (`claude-code/claude-opus-5` genuinely has none), while an
 * absent field means the question was never asked. Both produce numerically
 * identical `attributable*` figures, so nothing downstream can tell them
 * apart from the numbers alone — which is why the distinction is carried
 * separately into `gradeSkuTraffic` and comes out as `unknown`.
 */
export function refusedBeforeUpstreamOf(metrics: MaybeRefusalMetrics): number | null {
  return metrics.refusedBeforeUpstreamCount ?? null;
}

/** True when this row can answer the refusal question at all. */
export function reportsRefusals(metrics: MaybeRefusalMetrics): boolean {
  return refusedBeforeUpstreamOf(metrics) !== null;
}

/**
 * The supply-side counters (#2240), each `null` when this gateway does not
 * publish it — unknown, never zero, same rule as the refusal counter.
 */
interface MaybeSupplyMetrics {
  readonly requestCount: number;
  readonly refusedBeforeUpstreamCount?: number | null;
  readonly upstreamAttemptFailureCount?: number | null;
  readonly upstreamAttemptRateLimitedCount?: number | null;
  readonly upstreamRefusedCount?: number | null;
  readonly upstreamFinalFailureCount?: number | null;
}

/**
 * The five supply fields of `ObservedTraffic` from one wire row. The
 * arithmetic lives in `supplyFailureFigures` so this page and the traffic
 * monitor cannot disagree about the denominator.
 */
function supplyFigures(metrics: MaybeSupplyMetrics): {
  readonly upstreamAttemptFailures: number | null;
  readonly upstreamAttemptRateLimited: number | null;
  readonly upstreamRefused: number | null;
  readonly upstreamFinalFailures: number | null;
  readonly supplyFailures: number | null;
  readonly supplyFailureRate: number | null;
} {
  const upstreamAttemptFailures = metrics.upstreamAttemptFailureCount ?? null;
  const upstreamFinalFailures = metrics.upstreamFinalFailureCount ?? null;
  const figures = supplyFailureFigures({
    requests: metrics.requestCount,
    refusedBeforeUpstream: refusedBeforeUpstreamOf(metrics),
    upstreamAttemptFailures,
    upstreamFinalFailures,
  });
  return {
    upstreamAttemptFailures,
    upstreamAttemptRateLimited: metrics.upstreamAttemptRateLimitedCount ?? null,
    upstreamRefused: metrics.upstreamRefusedCount ?? null,
    upstreamFinalFailures,
    supplyFailures: figures === null ? null : figures.failures,
    supplyFailureRate: figures === null ? null : figures.rate,
  };
}

/** Sum a nullable counter across children; one unreported child poisons the parent. */
function sumNullable(
  children: readonly ObservedTraffic[],
  pick: (child: ObservedTraffic) => number | null,
): number | null {
  return children.reduce<number | null>((sum, child) => {
    const value = pick(child);
    return sum === null || value === null ? null : sum + value;
  }, 0);
}

/**
 * The three derived downstream figures, from the counts that produced them.
 *
 * `refusedBeforeUpstream` is a strict subset of `errors`, so it leaves BOTH
 * sides of the ratio and the subtraction is sound — unlike `errors -
 * serverErrors`, which mixes two populations. `Math.max(0, …)` guards the one
 * case the subset property does not: a gateway publishing counts from two
 * different aggregations. It clamps rather than going negative, and the
 * clamped state is indistinguishable from "everything was refused", which
 * grades `idle` rather than healthy.
 */
function downstreamFigures(
  requests: number,
  errors: number,
  refusedBeforeUpstream: number | null,
  byCode: Readonly<Record<string, number>> | null | undefined,
): {
  readonly refusedBeforeUpstream: number | null;
  readonly refusedByCode: readonly RefusalGroup[] | null;
  readonly expectedRefusals: number | null;
  readonly attributableRequests: number | null;
  readonly attributableErrors: number | null;
  readonly downstreamErrorRate: number | null;
} {
  const groups = refusalGroups(byCode);
  // Fail CLOSED on an absent code split: deduct NOTHING rather than deducting
  // the whole structural scalar. The scalar contains `MODEL_NOT_AVAILABLE`
  // and every code invented after this was written, so using it here would
  // quietly credit us for our own inability to serve — an error that can only
  // move the number in our favour, which is the one direction this page must
  // never take silently.
  // Null when the code split is absent — that is UNKNOWN, and the render says
  // so. The arithmetic below still deducts nothing, which is the fail-closed
  // reading of the same fact; the two must not be collapsed into one value,
  // or the caveat disappears from the screen.
  const expectedRefusals = expectedRefusalCount(byCode);
  if (refusedBeforeUpstream === null) {
    // The counter itself is absent: nothing downstream can be stated at all.
    // Null, never zero — a zero here reads as "none were refused", which on a
    // pre-counter gateway renders every failure as ours and states it as fact.
    return {
      refusedBeforeUpstream,
      refusedByCode: groups,
      expectedRefusals: null,
      attributableRequests: null,
      attributableErrors: null,
      downstreamErrorRate: null,
    };
  }
  const deducted = expectedRefusals ?? 0;
  const attributableRequests = Math.max(0, requests - deducted);
  const attributableErrors = Math.max(0, errors - deducted);
  return {
    refusedBeforeUpstream,
    refusedByCode: groups,
    expectedRefusals,
    attributableRequests,
    attributableErrors,
    // `rate` returns 0 for a zero denominator, so an all-refused window never
    // divides by zero. Grading treats that case as `idle`, not as 0% healthy.
    downstreamErrorRate: rate(attributableErrors, attributableRequests),
  };
}

/**
 * A node nothing measured. Distinct from "a node we failed to measure" —
 * that difference is carried by the grade (`idle` vs `unknown`), not by the
 * traffic figures, which are genuinely zero in both cases.
 */
export const EMPTY_TRAFFIC: ObservedTraffic = {
  requests: 0,
  errors: 0,
  errorRate: 0,
  serverErrors: 0,
  serverErrorRate: 0,
  rateLimited: 0,
  rateLimitedRate: 0,
  // Reported zeros, like `refusedBeforeUpstream` below: nothing was dialled,
  // so nothing failed — a measurement, not an absence.
  upstreamAttemptFailures: 0,
  upstreamAttemptRateLimited: 0,
  upstreamRefused: 0,
  upstreamFinalFailures: 0,
  supplyFailures: 0,
  supplyFailureRate: 0,
  refusedBeforeUpstream: 0,
  // `[]`, not `null`. Null means "the split was not reported"; an empty map
  // means "reported, and there were none" — which is necessarily true when
  // nothing was refused. Null here propagated through `mergeRefusalCodes`
  // (any null child poisons the parent), so one configured-but-silent SKU
  // made its whole namespace unable to classify, and the namespace's
  // downstream rate silently fell back to the raw error rate. ~50 catalog
  // SKUs against 31 with traffic means almost every namespace hit it.
  refusedByCode: [],
  expectedRefusals: 0,
  attributableRequests: 0,
  attributableErrors: 0,
  downstreamErrorRate: 0,
  fallbacks: 0,
  fallbackRate: 0,
  slowFirstByteRate: 0,
  observedShare: 0,
  avgFirstByteMs: null,
  avgTotalMs: null,
};

/**
 * The money shape for a node whose ledger rows do not exist — either because
 * rating has not settled the window yet, or because the money slice failed to
 * load at all. `usageCount` is 0 because the field is not nullable; `settled`
 * is the flag consumers must read, and a 0 here is not evidence of anything.
 */
export const UNSETTLED_MONEY: NodeMoney = {
  settled: false,
  usageCount: 0,
  revenueCnyCents: null,
  upstreamCostCnyCents: null,
  profitCnyCents: null,
  marginPct: null,
};

/**
 * One traffic breakdown row → the measured facts for its node.
 *
 * `requestShare` is taken verbatim as `observedShare`: the gateway computed it
 * against the whole window's request count under the same `trafficClass`
 * filter, and recomputing it here from a truncated breakdown list would
 * produce a share of the visible rows rather than of the fleet.
 */
export function trafficFromRow(row: OpsTrafficBreakdownRow): ObservedTraffic {
  return {
    requests: row.requestCount,
    errors: row.errorCount,
    errorRate: row.errorRate,
    serverErrors: row.serverErrorCount,
    // Divided here because the wire carries no such rate — see the header.
    serverErrorRate: rate(row.serverErrorCount, row.requestCount),
    rateLimited: row.rateLimitedCount,
    rateLimitedRate: rate(row.rateLimitedCount, row.requestCount),
    ...supplyFigures(row),
    // A gateway that does not publish the count contributes 0 here, which
    // makes `downstreamErrorRate` collapse to `errorRate`. That is NOT
    // presented as fact: `reportsRefusals` is recorded alongside and the
    // node grades `unknown` — see `gradeSkuTraffic`.
    ...downstreamFigures(
      row.requestCount,
      row.errorCount,
      refusedBeforeUpstreamOf(row),
      row.refusedByCode,
    ),
    fallbacks: row.fallbackCount,
    fallbackRate: row.fallbackRate,
    slowFirstByteRate: row.slowFirstByteRate,
    observedShare: row.requestShare,
    avgFirstByteMs: row.avgFirstByteMs,
    avgTotalMs: row.avgTotalMs,
  };
}

/**
 * The fleet summary metrics → the fleet node's measured facts.
 *
 * `observedShare` is 1 when anything was served: the fleet's share of itself.
 * It is 0 for an empty window rather than 1 so the figure never claims a
 * denominator that does not exist.
 */
export function trafficFromSummary(summary: OpsTrafficMetrics): ObservedTraffic {
  return {
    requests: summary.requestCount,
    errors: summary.errorCount,
    errorRate: summary.errorRate,
    serverErrors: summary.serverErrorCount,
    serverErrorRate: rate(summary.serverErrorCount, summary.requestCount),
    rateLimited: summary.rateLimitedCount,
    rateLimitedRate: rate(summary.rateLimitedCount, summary.requestCount),
    ...supplyFigures(summary),
    ...downstreamFigures(
      summary.requestCount,
      summary.errorCount,
      refusedBeforeUpstreamOf(summary),
      summary.refusedByCode,
    ),
    fallbacks: summary.fallbackCount,
    fallbackRate: summary.fallbackRate,
    slowFirstByteRate: summary.slowFirstByteRate,
    observedShare: summary.requestCount > 0 ? 1 : 0,
    avgFirstByteMs: summary.avgFirstByteMs,
    avgTotalMs: summary.avgTotalMs,
  };
}

/** Request-weighted mean over the children that reported a latency at all. */
function weightedLatency(
  children: readonly ObservedTraffic[],
  pick: (child: ObservedTraffic) => number | null,
): number | null {
  let weighted = 0;
  let weight = 0;
  for (const child of children) {
    const value = pick(child);
    if (value === null || child.requests <= 0) continue;
    weighted += value * child.requests;
    weight += child.requests;
  }
  return weight > 0 ? weighted / weight : null;
}

/**
 * Roll several nodes' measured facts into their parent (namespace ← SKUs).
 *
 * Rates are recomputed from the summed numerators, never averaged: averaging
 * a 100%-error 3-request child with a 0%-error 30000-request child yields 50%,
 * which is not a fact about anything. `slowFirstByteRate` has no count on
 * `ObservedTraffic`, so it is reconstructed as `rate * requests` per child —
 * exactly the count the gateway divided, so the sum is exact rather than an
 * approximation.
 */
export function aggregateTraffic(children: readonly ObservedTraffic[]): ObservedTraffic {
  const requests = children.reduce((sum, child) => sum + child.requests, 0);
  const errors = children.reduce((sum, child) => sum + child.errors, 0);
  const serverErrors = children.reduce((sum, child) => sum + child.serverErrors, 0);
  const rateLimited = children.reduce((sum, child) => sum + child.rateLimited, 0);
  // A parent whose children cannot all state their refusals cannot state its
  // own. Null wins, exactly as it does for one node.
  const refused = children.reduce<number | null>(
    (sum, child) =>
      sum === null || child.refusedBeforeUpstream === null
        ? null
        : sum + child.refusedBeforeUpstream,
    0,
  );
  const mergedByCode = mergeRefusalCodes(children);
  const upstreamAttemptFailures = sumNullable(children, (child) => child.upstreamAttemptFailures);
  const upstreamFinalFailures = sumNullable(children, (child) => child.upstreamFinalFailures);
  // Recomputed from the summed counts — the same helper, same denominator.
  const supply = supplyFailureFigures({
    requests,
    refusedBeforeUpstream: refused,
    upstreamAttemptFailures,
    upstreamFinalFailures,
  });
  const fallbacks = children.reduce((sum, child) => sum + child.fallbacks, 0);
  const slowFirstByte = children.reduce(
    (sum, child) => sum + child.slowFirstByteRate * child.requests,
    0,
  );
  const observedShare = children.reduce((sum, child) => sum + child.observedShare, 0);
  return {
    requests,
    // Summed independently, never combined: a parent's `serverErrors` is not
    // a share of its `errors` and the two overlap only by coincidence.
    errors,
    errorRate: rate(errors, requests),
    serverErrors,
    serverErrorRate: rate(serverErrors, requests),
    rateLimited,
    rateLimitedRate: rate(rateLimited, requests),
    upstreamAttemptFailures,
    upstreamAttemptRateLimited: sumNullable(children, (child) => child.upstreamAttemptRateLimited),
    upstreamRefused: sumNullable(children, (child) => child.upstreamRefused),
    upstreamFinalFailures,
    supplyFailures: supply === null ? null : supply.failures,
    supplyFailureRate: supply === null ? null : supply.rate,
    // Recomputed from the summed counts, never averaged from the children's
    // rates: a namespace's downstream rate is its own attributable errors over
    // its own attributable requests.
    ...downstreamFigures(requests, errors, refused, mergedByCode),
    fallbacks,
    fallbackRate: rate(fallbacks, requests),
    slowFirstByteRate: rate(slowFirstByte, requests),
    observedShare,
    avgFirstByteMs: weightedLatency(children, (child) => child.avgFirstByteMs),
    avgTotalMs: weightedLatency(children, (child) => child.avgTotalMs),
  };
}

/**
 * The two answers every grader gives before it looks at any rate.
 *
 *   - `trafficLoaded === false` → `unknown`. The slice that would decide this
 *     did not load; painting it `idle` would turn a broken read into a
 *     reassuring grey dot.
 *   - zero requests → `idle`. Nothing was served, so there is nothing to
 *     judge — and a SKU that is configured but silent is exactly the finding
 *     this page exists to surface, so it must not grade `ok`.
 */
function ungradable(traffic: ObservedTraffic, trafficLoaded: boolean): HealthGrade | null {
  if (!trafficLoaded) return 'unknown';
  if (traffic.requests <= 0) return 'idle';
  return null;
}

function fromSeverity(severity: TrafficSeverity): HealthGrade {
  if (severity === 'critical') return 'bad';
  if (severity === 'warning') return 'warn';
  return 'ok';
}

/**
 * Grade a DOWNSTREAM subject — a SKU, and through it a namespace and the fleet
 * headline.
 *
 * `downstreamErrorRate`, NOT `errorRate`. Two subtractions have already been
 * applied to get here and both are deliberate:
 *
 *   - a fallback-rescued request is absent from `errors` by the gateway's own
 *     SQL, because the customer was served,
 *   - a refused-before-upstream request is removed from BOTH sides of the
 *     ratio, because our gate declining it is the system working, not an
 *     outage we suffered.
 *
 * Production 24h: `claude-code/claude-fable-5` reads 76.7% on `errorRate` and
 * 0.5% on `downstreamErrorRate` (11 of 2,148), while `claude-code/claude-opus-5`
 * has 93 failures and zero refusals and is the SKU actually worth looking at.
 * Grading on `errorRate` inverts which of those two an operator investigates.
 *
 * `slowFirstByteSeverity` rides alongside because slow first byte is genuinely
 * tenant-facing — it is part of what the customer experienced. `fallbackRate`
 * does NOT: a fired ladder is a supply signal, and the tenant was served.
 *
 * `refusalsReported` is the third state the numbers cannot express. When the
 * gateway does not publish a refusal count, `refusedBeforeUpstream` is 0 and
 * `downstreamErrorRate` collapses to `errorRate` — numerically identical to a
 * SKU with genuinely no refusals. Substituting that zero and grading on it is
 * exactly the fable-5 mistake, so an unreported node grades `unknown`: the
 * slice that would decide it did not load.
 */
export function gradeSkuTraffic(
  traffic: ObservedTraffic,
  trafficLoaded: boolean,
  refusalsReported: boolean,
): HealthGrade {
  const undecidable = ungradable(traffic, trafficLoaded);
  if (undecidable !== null) return undecidable;
  // After `idle`, so a silent SKU still reads as silent rather than as a
  // broken read — there is nothing to refuse when nothing was served.
  if (!refusalsReported) return 'unknown';
  // Everything was refused: we served nothing we are answerable for. `idle`
  // for the same reason a zero-request node is idle, and never 0% healthy.
  if (traffic.attributableRequests === null) return 'unknown';
  if (traffic.attributableRequests <= 0) return 'idle';
  // `downstreamSeverity`, NOT `errorSeverity`: the latter criticals on 25
  // absolute failures at any denominator, which at SKU volumes paints every
  // busy row critical (production 24h: grok-4.6 at 49/3,552 = 1.4%, sonnet-5
  // at 40/2,539 = 1.6%) and hides the one row that is genuinely broken. See
  // that function's comment for why dropping the count arm is safe here.
  //
  // Latency is NOT in this verdict, and its absence is deliberate. "Slow"
  // is a fixed global 10s (`SLOW_FIRST_BYTE_MS`), so an image or video SKU
  // is ~100% slow by construction. Measured 24h: `codex/gpt-image-2` 88.6%
  // slow at 34.8s average with ZERO failures, and
  // `claude-code-api/claude-sonnet-5` 50% slow off one slow request out of
  // two. Both graded 严重 on latency alone while every failure column read
  // 0.0% — a verdict with no visible cause, on a rule with no sample floor.
  //
  // A served-but-slow request is a served request. Judging it needs a
  // per-SKU baseline no surface publishes; until one exists the page shows
  // latency and does not grade on it. See `slowFirstByteSeverity`.
  // Both are non-null past the guards above; the checks keep the types honest
  // rather than asserting.
  if (traffic.downstreamErrorRate === null) return 'unknown';
  return fromSeverity(
    downstreamSeverity(traffic.downstreamErrorRate, traffic.attributableRequests),
  );
}

/**
 * The rate a SUPPLY subject is graded and displayed on: the #2240 figure —
 * extra failed dials + failed final dials, over dials (requests that reached
 * an upstream + extra dials) — when the gateway reports it, else the
 * 5xx-only floor it reported before. `serverErrors` / `upstreamRefused` are
 * classification only and are not in it. Never total errors — see
 * `gradeSupplyTraffic`.
 */
export function supplyRateOf(traffic: ObservedTraffic): number {
  return traffic.supplyFailureRate ?? traffic.serverErrorRate;
}

/** The count behind `supplyRateOf`, on the same fallback. */
export function supplyFailuresOf(traffic: ObservedTraffic): number {
  return traffic.supplyFailures ?? traffic.serverErrors;
}

/**
 * The population `supplyRateOf` is over: every dial (requests that reached an
 * upstream + extra dials), or plain requests on the 5xx floor.
 */
export function supplyDialsOf(traffic: ObservedTraffic): number {
  if (traffic.supplyFailureRate === null) return traffic.requests;
  return Math.max(0, traffic.requests - (traffic.refusedBeforeUpstream ?? 0)) + (traffic.upstreamAttemptFailures ?? 0);
}

/**
 * Grade a SUPPLY subject — a channel, and every upstream model borrowing from
 * it.
 *
 * `supplyRateOf` and nothing else, on the gateway's own unhealthy-upstream
 * thresholds. Grading supply on total errors is the mistake this split exists
 * to prevent: production's `ndtpool-anthropic-oauth` ran a 41.0% error rate for
 * 24h with 30 of 7,604 errors being 5xx — a healthy pool being blamed for one
 * tenant's admission-gate refusals. Grading it on 5xx ALONE was the opposite
 * mistake (#2240): an upstream answering 402 to every request, or a pool
 * 429-ing every first attempt, read 0.0%.
 *
 * Below `SUPPLY_MIN_REQUESTS` dials this is `ok`, not `bad`, however bad the
 * rate looks: `supplySeverity` owns that floor and the gateway's rule has no
 * absolute-count escape hatch.
 */
export function gradeSupplyTraffic(traffic: ObservedTraffic, trafficLoaded: boolean): HealthGrade {
  return (
    ungradable(traffic, trafficLoaded) ??
    fromSeverity(supplySeverity(supplyRateOf(traffic), supplyDialsOf(traffic)))
  );
}

/**
 * Worst grade among children, for namespace and fleet roll-up.
 *
 * Empty input is `unknown`, not `ok`: a parent with no children to judge has
 * not been shown to be healthy.
 */
export function worstGrade(grades: readonly HealthGrade[]): HealthGrade {
  if (grades.length === 0) return 'unknown';
  return grades.reduce<HealthGrade>(
    (worst, next) =>
      HEALTH_GRADE_SEVERITY[next] > HEALTH_GRADE_SEVERITY[worst] ? next : worst,
    'ok',
  );
}

/**
 * SKU economics row → node money.
 *
 * `marginPct` is `l2MarginPct` verbatim. It is NOT recomputed from the cents
 * columns: the platform converts each ledger row at that row's own stored
 * `market_rate`, so a client-side `profit / revenue` would silently apply one
 * blended rate to a window that was actually rated at several, and disagree
 * with the 真实盈亏 page for the same interval.
 */
export function moneyFromSkuEconomics(row: BusinessSkuEconomicsRow): NodeMoney {
  if (row.usageCount <= 0) return UNSETTLED_MONEY;
  return {
    settled: true,
    usageCount: row.usageCount,
    revenueCnyCents: row.cnyChargedCents,
    upstreamCostCnyCents: row.cnyUpstreamCostCents,
    profitCnyCents: row.l2ProfitCnyCents,
    marginPct: row.l2MarginPct,
  };
}

/**
 * Revenue-by-upstream row → node money. Same verbatim-margin rule as above.
 *
 * Note the GRAIN: this endpoint groups by `upstream_id`, and the page's finest
 * measured supply node is an upstream CHANNEL, which is finer. The figure this
 * returns is therefore the whole upstream's, and the only node that can carry
 * it is a channel of that upstream — see `ChannelAccumulator.owningUpstreamMoney`
 * in `model-collect.ts` for what that costs and why nothing here splits it.
 */
export function moneyFromUpstreamRevenue(row: RevenueByUpstreamRow): NodeMoney {
  if (row.usage_count <= 0) return UNSETTLED_MONEY;
  return {
    settled: true,
    usageCount: row.usage_count,
    revenueCnyCents: row.cny_charged_cents,
    upstreamCostCnyCents: row.cny_upstream_cost_cents,
    profitCnyCents: row.l2_profit_cny_cents,
    marginPct: row.l2_margin_pct,
  };
}

/** Window totals → the fleet's blended money headline. */
export function moneyFromTotals(totals: BusinessSkuEconomicsTotals): NodeMoney {
  if (totals.usageCount <= 0) return UNSETTLED_MONEY;
  return {
    settled: true,
    usageCount: totals.usageCount,
    revenueCnyCents: totals.cnyChargedCents,
    upstreamCostCnyCents: totals.cnyUpstreamCostCents,
    profitCnyCents: totals.l2ProfitCnyCents,
    marginPct: totals.l2MarginPct,
  };
}

/**
 * Sum the children's code maps for a parent node.
 *
 * `null` when ANY child cannot report one: a partial map would let a parent
 * deduct refusals it can only account for on some of its children, which is
 * the same one-directional flattery as using the raw scalar.
 */
function mergeRefusalCodes(
  children: readonly ObservedTraffic[],
): Readonly<Record<string, number>> | null {
  const merged: Record<string, number> = {};
  for (const child of children) {
    if (child.refusedByCode === null) return null;
    for (const group of child.refusedByCode) {
      merged[group.code] = (merged[group.code] ?? 0) + group.count;
    }
  }
  return merged;
}
